/**
 * Reading the *other* IFC measures back off a candidate row.
 *
 * The parser picks one measure per IFC row (`deriveIfcQuantity`) and writes it
 * to the synthetic Quantity/Unit columns, but every measure column the export
 * carried is still on the row. When the mapped product/recipe cannot express
 * the picked unit, the mapping side walks these alternatives -- in the same
 * IFC-type priority order the parser used -- and imports the row in a unit the
 * target does support, instead of asking the user for a conversion factor the
 * file already answers.
 */
import type { CandidateRow } from '@shared/models/element-import.model';
import { headerKey } from '@shared/helpers/kalkylfil-csv.parser';
import {
  IFC_MEASURE_HEADER_KEYS,
  IFC_TYPE_HEADER_KEYS,
  UNIT_BY_MEASURE,
  getIfcMeasurePriority,
  isValidMeasure,
  type IfcMeasure,
} from '@shared/helpers/ifc-quantity.helpers';
import { toNumber } from '@shared/helpers/math.helpers';

/** One measure column of an IFC row that carries a usable value. */
export type CandidateIfcMeasure = {
  measure: IfcMeasure;
  /** Nodon unit for the measure ('m', 'm2', 'm3', 'pcs') -- same string the parser writes. */
  unit: string;
  quantity: number;
};

/** Trimmed value of the first cell whose header matches one of `keys`. */
const cellByHeaderKeys = (
  row: CandidateRow,
  keys: ReadonlySet<string>,
): string | undefined => {
  const cell = row.cells.find((c) =>
    keys.has(headerKey(c.originalHeader ?? '')),
  );
  const value = String(cell?.value ?? '').trim();
  return value.length > 0 ? value : undefined;
};

/**
 * The row's IFC type. `''` when the import carried an IFC type column but this
 * row's cell is blank (the parser's fallback priority still applies);
 * `undefined` when there is no IFC type column at all -- not an IFC import.
 */
export const candidateIfcType = (row: CandidateRow): string | undefined => {
  const cell = row.cells.find((c) =>
    IFC_TYPE_HEADER_KEYS.has(headerKey(c.originalHeader ?? '')),
  );
  return cell === undefined ? undefined : String(cell.value ?? '').trim();
};

/**
 * Every measure the row carries a valid value for, in the order the parser used
 * for this IFC type (its own, or the generic fallback). Empty for a non-IFC row.
 */
export const candidateIfcMeasures = (
  row: CandidateRow,
): CandidateIfcMeasure[] => {
  const ifcType = candidateIfcType(row);
  if (ifcType === undefined) return [];

  const measures: CandidateIfcMeasure[] = [];
  for (const measure of getIfcMeasurePriority(ifcType)) {
    const raw = cellByHeaderKeys(
      row,
      new Set([IFC_MEASURE_HEADER_KEYS[measure]]),
    );
    if (raw === undefined || !isValidMeasure(raw)) continue;
    measures.push({
      measure,
      unit: UNIT_BY_MEASURE[measure],
      quantity: toNumber(raw, 0),
    });
  }
  return measures;
};
